import { useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { ContentContainer, PageLayout } from "components/layout";
import {
  useGetProcessedDataQuery,
  type TaxClosingTabs,
} from "@/services/tax-closing";

import { PageHeader } from "./page-header";
import { ViewStateRenderer } from "./view-states";
import {
  DEFAULT_TAX_CLOSING_TAB,
  isTaxClosingTabKey,
  type TaxClosingTabKey,
} from "./tabs-config";
import { resolveViewState } from "./view-state";
import { useSituationFilter } from "../hooks/use-situation-filter";
import { SITUATION_ALL, shouldFallbackToAll } from "../hooks/situation-filter";
import { updateSearchParam } from "../hooks/update-search-param";

const BACK_PATH = "/tax/closing";

export function DataVisualizationPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { situation, filters, setSituation } = useSituationFilter();

  const tabParam = searchParams.get("tab");
  const activeTab: TaxClosingTabKey = isTaxClosingTabKey(tabParam)
    ? tabParam
    : DEFAULT_TAX_CLOSING_TAB;

  const handleTabChange = (value: string) => {
    updateSearchParam(setSearchParams, "tab", value);
  };

  const { data, isLoading, isError, error } = useGetProcessedDataQuery(
    { jobId: jobId!, filters },
    { skip: !jobId },
  );

  const fiscalData: TaxClosingTabs | null = data?.data ?? null;
  const situationOptions = useMemo(() => data?.facets?.situacao ?? [], [data]);
  const errorStatus = (error as { status?: number } | undefined)?.status;

  useEffect(() => {
    if (isLoading || !data) return;
    if (shouldFallbackToAll(situation, situationOptions)) setSituation(SITUATION_ALL);
  }, [isLoading, data, situation, situationOptions, setSituation]);

  const viewState = resolveViewState({
    isLoading,
    isError,
    errorStatus,
    hasData: fiscalData !== null,
  });

  const goBack = () => navigate(BACK_PATH);

  return (
    <PageLayout>
      <PageHeader
        jobId={jobId}
        summary={fiscalData?.summary}
        hasData={fiscalData !== null}
        onBack={goBack}
      />

      <ContentContainer>
        <ViewStateRenderer
          state={viewState}
          fiscalData={fiscalData}
          jobId={jobId}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onBack={goBack}
        />
      </ContentContainer>
    </PageLayout>
  );
}
